import {
  canCompanyWide,
  NORM_KINDS,
  NORM_STATUSES,
  type NormImportResult,
  type NormKind,
  type NormReleaseSummary,
  type NormSearchResult,
  type NormStatus,
  type NormsOverview,
  RESOURCE_KINDS,
} from '@epc/contracts';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { FileUp, Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Page, PageHeader, Panel, TitleBlock } from '../../ui/layout';
import { hours, sum } from '../estimates/lib';
import { LibraryPanel } from './Library';

export const normsQuery = queryOptions({ queryKey: ['norms'], queryFn: () => api<NormsOverview>('/norms') });
const searchQuery = (q: string) =>
  queryOptions({
    queryKey: ['norms', 'search', q],
    queryFn: () => api<NormSearchResult>('/norms/search', { query: { q, limit: 60 } }),
    enabled: q.trim().length >= 2,
  });

const STATUS_TONE: Record<NormStatus, 'success' | 'warning' | 'danger' | 'neutral'> = {
  active: 'success',
  replaced: 'neutral',
  inactive: 'danger',
  check: 'warning',
};

export function NormStatusBadge({ status }: { status: NormStatus }) {
  return <Badge tone={STATUS_TONE[status]}>{NORM_STATUSES[status]}</Badge>;
}

function ReleaseDialog({
  r,
  releases,
  onClose,
}: {
  r: NormReleaseSummary;
  releases: NormReleaseSummary[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    name: r.name,
    nameKk: r.nameKk ?? '',
    kind: r.kind as NormKind,
    year: r.year ? String(r.year) : '',
    issue: r.issue ?? '',
    priceZone: r.priceZone ?? '',
    priceLevel: r.priceLevel ?? '',
    orderNo: r.orderNo ?? '',
    effectiveFrom: r.effectiveFrom ?? '',
    replacesId: r.replacesId ?? '',
    status: r.status as NormStatus,
    statusSource: r.statusSource?.startsWith('Создан при импорте') ? '' : (r.statusSource ?? ''),
    languages: r.languages.join(','),
    note: r.note ?? '',
  });
  const m = useMutation({
    mutationFn: () =>
      api(`/norms/releases/${r.id}`, {
        method: 'PATCH',
        body: {
          name: f.name.trim(),
          nameKk: f.nameKk.trim() || null,
          kind: f.kind,
          year: f.year ? Number(f.year) : null,
          issue: f.issue.trim() || null,
          priceZone: f.priceZone.trim() || null,
          priceLevel: f.priceLevel.trim() || null,
          orderNo: f.orderNo.trim() || null,
          effectiveFrom: f.effectiveFrom || null,
          replacesId: f.replacesId || null,
          status: f.status,
          statusSource: f.statusSource.trim() || undefined,
          languages: f.languages
            .split(',')
            .map((x) => x.trim())
            .filter((x): x is 'ru' | 'kk' => x === 'ru' || x === 'kk'),
          note: f.note.trim() || null,
        },
      }),
    onSuccess: () => {
      toast('Карточка документа сохранена');
      qc.invalidateQueries({ queryKey: ['norms'] });
      onClose();
    },
  });
  const set = (p: Partial<typeof f>) => setF({ ...f, ...p });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={r.code}
      description="Карточка документа нормативной библиотеки (БЗ-12): редакция, приказ, статус действия и его источник"
      width="lg"
      footer={
        <>
          <Button className="min-h-[44px] md:min-h-0" variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            className="min-h-[44px] md:min-h-0"
            variant="primary"
            loading={m.isPending}
            onClick={() => m.mutate()}
          >
            Сохранить
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Наименование" className="sm:col-span-2">
          <Input value={f.name} onChange={(e) => set({ name: e.target.value })} />
        </Field>
        <Field label="Наименование на казахском" className="sm:col-span-2">
          <Input value={f.nameKk} onChange={(e) => set({ nameKk: e.target.value })} />
        </Field>
        <Field label="Вид документа">
          <Select value={f.kind} onChange={(e) => set({ kind: e.target.value as NormKind })}>
            {(Object.keys(NORM_KINDS) as NormKind[]).map((k) => (
              <option key={k} value={k}>
                {NORM_KINDS[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Год редакции">
          <Input inputMode="numeric" value={f.year} onChange={(e) => set({ year: e.target.value })} />
        </Field>
        <Field label="Выпуск изменений">
          <Input value={f.issue} onChange={(e) => set({ issue: e.target.value })} />
        </Field>
        <Field label="Ценовая зона">
          <Input
            value={f.priceZone}
            onChange={(e) => set({ priceZone: e.target.value })}
            placeholder="6.7.00"
          />
        </Field>
        <Field label="Уровень цен" className="sm:col-span-2">
          <Input
            value={f.priceLevel}
            onChange={(e) => set({ priceLevel: e.target.value })}
            placeholder="текущие цены июля 2026 года"
          />
        </Field>
        <Field label="Приказ об утверждении">
          <Input
            value={f.orderNo}
            onChange={(e) => set({ orderNo: e.target.value })}
            placeholder="№ 67-НҚ от 18.06.2026"
          />
        </Field>
        <Field label="Введён с">
          <Input
            type="date"
            value={f.effectiveFrom}
            onChange={(e) => set({ effectiveFrom: e.target.value })}
          />
        </Field>
        <Field label="Заменяет документ" className="sm:col-span-2">
          <Select value={f.replacesId} onChange={(e) => set({ replacesId: e.target.value })}>
            <option value="">—</option>
            {releases
              .filter((x) => x.id !== r.id)
              .map((x) => (
                <option key={x.id} value={x.id}>
                  {x.code}
                  {x.priceZone ? ` (${x.priceZone})` : ''}
                </option>
              ))}
          </Select>
        </Field>
        <Field label="Статус действия">
          <Select value={f.status} onChange={(e) => set({ status: e.target.value as NormStatus })}>
            {(Object.keys(NORM_STATUSES) as NormStatus[]).map((k) => (
              <option key={k} value={k}>
                {NORM_STATUSES[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Языки">
          <Input
            value={f.languages}
            onChange={(e) => set({ languages: e.target.value })}
            placeholder="ru,kk"
          />
        </Field>
        <Field
          label="Источник статуса"
          hint="Текст документа («введён с», «взамен»), перечень АГСК-1 или АГСК-5, заметка сметного отдела (БЗ-13)"
          className="sm:col-span-2"
        >
          <Input value={f.statusSource} onChange={(e) => set({ statusSource: e.target.value })} />
        </Field>
        <Field label="Примечание" className="sm:col-span-2">
          <Textarea rows={3} value={f.note} onChange={(e) => set({ note: e.target.value })} />
        </Field>
      </div>
      <ErrorBox error={m.error} className="mt-4" />
    </Dialog>
  );
}

function ImportNorms({ onDone }: { onDone: (r: NormImportResult) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const m = useMutation({
    mutationFn: (files: FileList) => {
      const form = new FormData();
      for (const f of Array.from(files)) form.append('files', f);
      return api<NormImportResult>('/norms/import', { form });
    },
    onSuccess: onDone,
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  return (
    <>
      <input
        ref={ref}
        type="file"
        multiple
        accept=".kenml,.xml"
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) m.mutate(e.target.files);
          e.target.value = '';
        }}
      />
      <Button
        className="min-h-[44px] md:min-h-0"
        variant="primary"
        icon={<FileUp className="size-4" />}
        loading={m.isPending}
        onClick={() => ref.current?.click()}
      >
        Пополнить из KENML
      </Button>
    </>
  );
}

export function NormsPage() {
  const me = useMe();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [typed, setTyped] = useState('');
  const [edit, setEdit] = useState<NormReleaseSummary | null>(null);
  const [kindFilter, setKindFilter] = useState<string>('');
  const ov = useQuery(normsQuery);
  const found = useQuery(searchQuery(q));
  const manage = canCompanyWide(me.access, 'norms.manage');
  useCrumbs([]);
  useEffect(() => {
    const t = setTimeout(() => setQ(typed), 300);
    return () => clearTimeout(t);
  }, [typed]);

  const onImported = (r: NormImportResult) => {
    qc.invalidateQueries({ queryKey: ['norms'] });
    toast(
      `Добавлено норм: ${r.itemsAdded}, ресурсов: ${r.resourcesAdded}${r.issues.length ? `; замечаний: ${r.issues.length}` : ''}`,
    );
  };

  const releases = (ov.data?.releases ?? []).filter((r) => !kindFilter || r.kind === kindFilter);
  const kinds = [...new Set((ov.data?.releases ?? []).map((r) => r.kind))];

  return (
    <Page>
      <PageHeader
        title="Нормативная база"
        subtitle="Сборники и нормы РК по выпускам: ЭСН, ССЦ, СЦЭМ, СЦЗТ, индексы, классификатор ресурсов. Поиск по коду и обычными словами"
        actions={manage ? <ImportNorms onDone={onImported} /> : null}
      >
        <div className="relative mt-3 max-w-xl">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-2" />
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="свая 400, 1310-0701, дюбель…"
            className="pl-9"
            aria-label="Поиск по нормативной базе"
          />
        </div>
      </PageHeader>

      {q.trim().length >= 2 ? (
        <div className="mb-6 flex flex-col gap-4">
          {found.isPending ? (
            <Spinner label="Поиск" />
          ) : found.isError ? (
            <ErrorBox error={found.error} />
          ) : (
            <>
              <Panel title="Нормы и расценки" meta={`${found.data.items.length}`} bodyClassName="p-0">
                {found.data.items.length ? (
                  <ul className="divide-y divide-line">
                    {found.data.items.map((it) => (
                      <li key={it.id}>
                        <Link
                          to="/norms/items/$itemId"
                          params={{ itemId: it.id }}
                          className="flex min-h-[44px] flex-col gap-1 px-5 py-3 hover:bg-surface-2 md:flex-row md:items-center md:gap-4"
                        >
                          <span className="num w-[150px] shrink-0 text-[13px] text-ink">{it.code}</span>
                          <span className="min-w-0 flex-1 text-[14px] [overflow-wrap:anywhere]">
                            {it.name}
                          </span>
                          <span className="flex flex-wrap items-center gap-2 text-[12px] text-ink-2">
                            <span>{it.unit}</span>
                            <Badge>{it.releaseCode}</Badge>
                            {it.laborHours ? (
                              <span className="num">{hours(it.laborHours)} чел.-ч</span>
                            ) : null}
                            <span className="num text-ink">
                              {it.unitDirect !== null ? `${sum(it.unitDirect)} ₸` : ''}
                            </span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="px-5 py-4 text-[13px] text-ink-2">Норм не найдено.</p>
                )}
              </Panel>
              <Panel
                title="Ресурсы (материалы, машины, труд)"
                meta={`${found.data.resources.length}`}
                bodyClassName="p-0"
              >
                {found.data.resources.length ? (
                  <ul className="divide-y divide-line">
                    {found.data.resources.map((r) => (
                      <li
                        key={r.id}
                        className="flex min-h-[44px] flex-col gap-1 px-5 py-3 md:flex-row md:items-center md:gap-4"
                      >
                        <span className="num w-[150px] shrink-0 text-[13px] text-ink">{r.code}</span>
                        <span className="min-w-0 flex-1 text-[14px] [overflow-wrap:anywhere]">{r.name}</span>
                        <span className="flex flex-wrap items-center gap-2 text-[12px] text-ink-2">
                          <Badge>{RESOURCE_KINDS[r.kind as keyof typeof RESOURCE_KINDS] ?? r.kind}</Badge>
                          <span>{r.unit}</span>
                          <Badge>{r.releaseCode}</Badge>
                          <span className="num text-ink">{r.price !== null ? `${sum(r.price)} ₸` : ''}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="px-5 py-4 text-[13px] text-ink-2">Ресурсов не найдено.</p>
                )}
              </Panel>
            </>
          )}
        </div>
      ) : null}

      {ov.isPending ? (
        <Spinner />
      ) : ov.isError ? (
        <ErrorBox error={ov.error} />
      ) : (
        <div className="flex flex-col gap-6">
          <TitleBlock
            items={[
              { label: 'Выпусков в базе', value: ov.data.releases.length },
              { label: 'Норм', value: ov.data.releases.reduce((s, r) => s + r.items, 0) },
              { label: 'Ресурсов с ценами', value: ov.data.releases.reduce((s, r) => s + r.resources, 0) },
              {
                label: 'Требуют сверки',
                value: ov.data.needCheck,
                note: 'в новые сметы не подставляются (БЗ-13)',
                tone: ov.data.needCheck ? 'danger' : 'default',
              },
            ]}
          />
          <Panel
            title="Выпуски и документы"
            meta="СМ-02, БЗ-12"
            bodyClassName="p-0"
            actions={
              kinds.length > 1 ? (
                <Select
                  value={kindFilter}
                  onChange={(e) => setKindFilter(e.target.value)}
                  aria-label="Вид документа"
                  className="h-9"
                >
                  <option value="">Все виды</option>
                  {kinds.map((k) => (
                    <option key={k} value={k}>
                      {NORM_KINDS[k as NormKind] ?? k}
                    </option>
                  ))}
                </Select>
              ) : null
            }
          >
            {releases.length ? (
              <ul className="divide-y divide-line">
                {releases.map((r) => (
                  <li
                    key={r.id}
                    className="flex flex-col gap-2 px-5 py-3 md:flex-row md:items-center md:gap-4"
                  >
                    <div className="min-w-0 flex-1">
                      <Link
                        to="/norms/releases/$releaseId"
                        params={{ releaseId: r.id }}
                        className="inline-flex min-h-[44px] items-center text-[14px] font-medium text-ink underline-offset-2 hover:underline md:min-h-0"
                      >
                        {r.code}
                      </Link>
                      <span className="block text-[13px] text-ink-2 [overflow-wrap:anywhere]">{r.name}</span>
                      <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-ink-2">
                        <span>{NORM_KINDS[r.kind] ?? r.kind}</span>
                        {r.priceZone ? <span>зона {r.priceZone}</span> : null}
                        {r.priceLevel ? <span>{r.priceLevel}</span> : null}
                        {r.orderNo ? <span>{r.orderNo}</span> : null}
                        {r.effectiveFrom ? <span>с {date(r.effectiveFrom)}</span> : null}
                        {r.replacesCode ? <span>взамен {r.replacesCode}</span> : null}
                        {r.replacedByCode ? <span>заменён: {r.replacedByCode}</span> : null}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-[12px] text-ink-2">
                      <NormStatusBadge status={r.status} />
                      {r.items ? <span className="num">{r.items} норм</span> : null}
                      {r.resources ? <span className="num">{r.resources} ресурсов</span> : null}
                      {r.estimates ? <span className="num">{r.estimates} смет</span> : null}
                      {manage ? (
                        <Button
                          variant="ghost"
                          className={clsx('min-h-[44px] md:min-h-0', 'min-h-[44px]')}
                          onClick={() => setEdit(r)}
                        >
                          Карточка
                        </Button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="p-5">
                <EmptyState title="База пуста" action={manage ? <ImportNorms onDone={onImported} /> : null}>
                  Выпуски появляются при загрузке смет KENML или пополнении из экспортов АВС-KZ. Загрузка
                  сборников с Диска целиком — следующим шагом (БЗ-14).
                </EmptyState>
              </div>
            )}
          </Panel>
          <LibraryPanel />
          <Panel title="Чего не хватает" meta="приложение А.4 ТЗ, БЗ-18" bodyClassName="p-0">
            <ul className="divide-y divide-line">
              {ov.data.missing.map((m) => (
                <li
                  key={`${m.code}-${m.what}`}
                  className="flex flex-col gap-1 px-5 py-3 text-[13px] md:flex-row md:items-center md:gap-4"
                >
                  <span className="w-[260px] shrink-0 font-medium text-ink">{m.code}</span>
                  <span className="min-w-0 flex-1 text-ink-2 [overflow-wrap:anywhere]">{m.what}</span>
                  <Badge>{m.owner}</Badge>
                </li>
              ))}
            </ul>
          </Panel>
        </div>
      )}
      {edit && ov.data ? (
        <ReleaseDialog r={edit} releases={ov.data.releases} onClose={() => setEdit(null)} />
      ) : null}
    </Page>
  );
}
